// src/components/recheck/recheckPosition.js
//
// A confirm's corrected position (DB 2026-10-25, migration 142): the reviewer
// drags the well to where it is on the item's own collect, at most 50 m from
// the item's stored point (10 m until DB 2026-10-26, migration 144). The
// server measures the same distance on the spheroid and refuses past the limit
// (422 position_too_far) or below a centimetre (400 position_unchanged); this
// module is the page's live copy of those two rules, so the button can say so
// before the server has to. Past 10 m the page warns, and still confirms.

/** admin-api's RECHECK_POSITION_LIMIT_M, and 144's _limit_m. */
export const RECHECK_POSITION_LIMIT_M = 50;
/** Past this the draft is amber and the bar asks the reviewer to check the well (DB 2026-10-26). */
export const RECHECK_POSITION_ADVISORY_M = 10;
/** Below this the position is the item's own (the server's POSITION_UNCHANGED_M). */
export const POSITION_UNCHANGED_M = 0.01;

const A = 6378137;                 // WGS84
const E2 = 6.69437999014e-3;

/**
 * Metres between two lon/lat points a few metres apart, from the WGS84 radii
 * of curvature at their mean latitude: within millimetres of PostGIS's
 * geography distance at this range.
 */
export function distanceMeters([lon1, lat1], [lon2, lat2]) {
  const r = Math.PI / 180;
  const phi = ((lat1 + lat2) / 2) * r;
  const s = 1 - E2 * Math.sin(phi) ** 2;
  const n = A / Math.sqrt(s);                    // prime vertical
  const m = (A * (1 - E2)) / (s * Math.sqrt(s)); // meridian
  return Math.hypot((lon2 - lon1) * r * n * Math.cos(phi), (lat2 - lat1) * r * m);
}

/**
 * The draft's standing against the item's point: its distance, whether the
 * server would refuse it (tooFar, unchanged), whether it is past the warning
 * but confirmable (advisory), and whether it is drawn amber (either).
 * @returns {{distance_m: number, tooFar: boolean, advisory: boolean, amber: boolean, unchanged: boolean}|null}
 */
export function draftState(origin, draft) {
  if (!origin || !draft) return null;
  const distance_m = distanceMeters(origin, draft);
  const tooFar = distance_m > RECHECK_POSITION_LIMIT_M;
  const amber = distance_m > RECHECK_POSITION_ADVISORY_M;
  return {
    distance_m,
    tooFar,
    advisory: amber && !tooFar,
    amber,
    unchanged: distance_m < POSITION_UNCHANGED_M,
  };
}

/** "4.3 m": the distance as the page shows it. */
export function formatMeters(d) {
  return `${d.toFixed(1)} m`;
}

/** The verdict's position, placed on the epoch on screen. */
export function positionPayload(draft, placedOnDatasetKey) {
  return { type: "Point", coordinates: [draft[0], draft[1]], placed_on_dataset_key: placedOnDatasetKey };
}
